/**
 * منطقِ خالصِ لاگِ تشخیصیِ روی دستگاه: شکلِ یک سطر، حلقه‌ی سطرها، متنِ
 * نهایی، و تصمیمِ «این دستگاه کند است، دیگر ننویس».
 *
 * از `diagLog.ts` جدا شده به همان دلیلی که `devlogRow` از `devlog` جدا شده:
 * آن یکی فایل و تایمر و شبکه دارد و این یکی هیچ‌کدام — پس این با `npm test`
 * مستقیم اجرا می‌شود، بدونِ گوشی و بدونِ مرورگر. آستانه‌های خاموش شدنِ
 * خودکار هم دقیقاً به همین دلیل این‌جایند و نه آن‌جا: عددی که رفتارِ اپ را
 * روی گوشیِ کاربر عوض می‌کند باید آزمودنی باشد.
 */
import type { DevLogKind, DevLogRow } from './devlogRow.ts';

/* ─────────────────────────── اندازه‌ها ─────────────────────────── */

/**
 * چند سطرِ آخر نگه داشته می‌شود.
 *
 * حلقه‌ست، نه فهرست: سیصدتایی که پر شد، هر سطرِ تازه قدیمی‌ترین را می‌اندازد.
 * سیصد سطر یعنی چند دقیقه‌ی آخرِ کار کردن با اپ — و همین چند دقیقه است که
 * وقتی کسی می‌گوید «الان خراب شد» اهمیت دارد. کلِ حافظه‌ای که می‌گیرد زیرِ
 * ۶۰ کیلوبایت است.
 */
export const KEEP = 300;
/** سقفِ هر سطر. یک پیامِ خطای چندخطی نباید ده سطرِ دیگر را از حلقه بیرون کند. */
export const MAX_LINE = 200;
/** سقفِ کلِ متنی که فرستاده می‌شود. */
export const MAX_REPORT = 60_000;
/** و سهمِ نشستِ قبلی از آن. */
export const MAX_PREVIOUS = 20_000;

/* ─────────────────────────── یک سطر ─────────────────────────── */

/**
 * برچسبِ سه‌حرفیِ هر نوع. هم‌عرض‌اند تا ستونِ بعدی سرِ جای خودش بماند و
 * چشم بتواند فایل را عمودی بخواند.
 */
const TAG: Record<DevLogKind, string> = {
  app: 'app',
  nav: 'nav',
  tap: 'tap',
  query: 'qry',
  error: 'ERR',
  console: 'con',
};

/** هر چیزی که توی یک سطر می‌نشیند: بدونِ خطِ جدید، بدونِ فاصله‌ی اضافه. */
const flat = (v: unknown): string => String(v).replace(/\s+/g, ' ').trim();

/**
 * یک ردیفِ لاگ → یک سطرِ متن.
 *
 *     12.3s nav  discover 340ms via=tap:btn:کشف
 *     12.9s qry  events.select 210ms rows=20
 *     13.1s ERR  feed.load ✘ message=network request failed
 *
 * `sinceMs` یعنی از بالا آمدنِ اپ تا حالا. ساعتِ دیواری نمی‌نویسیم چون
 * چیزی که موقعِ خواندن لازم است **فاصله**هاست («این کوئری سه ثانیه بعدِ آن
 * تپ رفت»)، نه اینکه ساعت چند بوده — و ساعتِ خودِ ردیف (`row.at`) اگر لازم
 * شد در سرآیند هست.
 *
 * موفقیت علامت نمی‌خورد و فقط شکست (`✘`) دیده می‌شود: در یک فایلِ سیصد
 * سطری، تیک کنارِ همه‌چیز یعنی هیچ‌چیز.
 */
export function formatDiag(row: DevLogRow, sinceMs: number): string {
  const t = `${(Math.max(0, sinceMs) / 1000).toFixed(1)}s`.padStart(7, ' ');
  const dur = row.ms == null ? '' : ` ${row.ms}ms`;
  const bad = row.ok === false ? ' ✘' : '';
  const det = Object.entries(row.detail)
    .map(([k, v]) => `${k}=${flat(v)}`)
    .join(' ');
  const line = `${t} ${TAG[row.kind]} ${flat(row.name)}${dur}${bad}${det ? ` ${det}` : ''}`;
  return line.length > MAX_LINE ? `${line.slice(0, MAX_LINE - 1)}…` : line;
}

/**
 * سطرِ تازه به حلقه.
 *
 * عمداً همان آرایه را دست‌کاری می‌کند و آرایه‌ی تازه نمی‌سازد: این تابع
 * ممکن است چند صد بار در دقیقه صدا زده شود و ساختنِ یک آرایه‌ی سیصدتاییِ
 * تازه در هر بار، دقیقاً همان هزینه‌ای است که این ویژگی قول داده ندهد.
 */
export function pushLine(lines: string[], line: string, keep: number = KEEP): void {
  lines.push(line);
  if (lines.length > keep) lines.splice(0, lines.length - keep);
}

/* ─────────────────────────── هزینه‌ی خودِ لاگ ─────────────────────────── */

/**
 * چقدر از وقتِ گوشی صرفِ خودِ لاگ شده.
 *
 * ═══ چرا این و نه مشخصاتِ دستگاه ═══
 *
 * «گوشی کند است» را می‌شد از رم یا نسخه‌ی اندروید حدس زد؛ ولی آن یک حدسِ
 * ثابت است که هیچ‌وقت با واقعیت سنجیده نمی‌شود. چیزی که واقعاً اهمیت دارد
 * این نیست که گوشی چند سالش است، این است که **نوشتنِ این لاگ روی این گوشی
 * چقدر طول می‌کشد**. پس همان را مستقیم اندازه می‌گیریم و اگر از بودجه‌ی
 * تعیین‌شده گذشت، لاگ خودش را خاموش می‌کند.
 */
export interface DiagCost {
  /** چند بار روی فایل نوشته‌ایم. */
  writes: number;
  /** از این‌ها چندتا کند بود. */
  slow: number;
  /** جمعِ میلی‌ثانیه‌هایی که این دستگاه صرفِ لاگ کرده (نوشتن + ثبتِ سطرها). */
  spentMs: number;
}

export const ZERO_COST: DiagCost = { writes: 0, slow: 0, spentMs: 0 };

/** نوشتنی که بیشتر از این طول بکشد، روی این دستگاه گران است. */
export const SLOW_WRITE_MS = 120;
/** و دو بارش یعنی اتفاقی نبوده. */
export const MAX_SLOW_WRITES = 2;
/**
 * سقفِ کلِ وقتی که یک نشست حق دارد صرفِ لاگ کند.
 *
 * نیم ثانیه در طولِ کلِ یک نشست (که معمولاً چند دقیقه است) عملاً نامرئی
 * است؛ از آن که گذشت دیگر داریم به کاربر هزینه تحمیل می‌کنیم تا خودمان
 * راحت‌تر دیباگ کنیم، و آن معامله‌ی بدی است.
 */
export const COST_BUDGET_MS = 500;

/**
 * یک نوشتن ثبت شد.
 *
 * ⚠ اولین نوشتن نه در بودجه حساب می‌شود نه کند به حساب می‌آید: بارِ اول
 * پوشه ساخته می‌شود، پلِ نیتیو گرم می‌شود و فایل از صفر ساخته می‌شود. آن
 * عدد هرچقدر هم بزرگ باشد چیزی درباره‌ی سرعتِ این گوشی نمی‌گوید، و بدونِ
 * این استثنا هر گوشیِ سالمی هم ممکن بود در همان ثانیه‌های اول خودش را
 * خاموش کند.
 */
export function noteWrite(c: DiagCost, ms: number): DiagCost {
  const first = c.writes === 0;
  return {
    writes: c.writes + 1,
    slow: c.slow + (!first && ms >= SLOW_WRITE_MS ? 1 : 0),
    spentMs: c.spentMs + (first ? 0 : Math.max(0, ms)),
  };
}

/**
 * سقفِ سهمِ یک نمونه. ثبتِ یک سطر یعنی یک رشته‌سازی و یک `push`؛ اگر عددی
 * بزرگ‌تر از این درآمد، آن عدد مالِ ما نیست — یک وقفه‌ی زباله‌روبی یا یک
 * سوییچِ نخ دقیقاً وسطِ نمونه‌گیری افتاده.
 */
export const SAMPLE_CAP_MS = 2;

/**
 * ثبتِ سطرها هم وقت می‌برد — ولی آن‌قدر کم که اندازه‌گیریِ تک‌تکشان از خودِ
 * کار گران‌تر می‌شود. پس هر `every` تا یکی زمان گرفته می‌شود و همان به کلِ
 * دسته تعمیم داده می‌شود.
 *
 * ⚠ تعمیم دادن یعنی خطا هم ضربدرِ `every` می‌شود: یک نمونه‌ی بیست
 * میلی‌ثانیه‌ای (که با کارِ ما هیچ ربطی ندارد) یک‌تنه ششصد میلی‌ثانیه به حساب
 * می‌نشیند و لاگ را همان‌جا می‌کشد. `SAMPLE_CAP_MS` جلوی همین را می‌گیرد —
 * کم‌تخمین زدن این‌جا از پرتخمین زدن بی‌خطرتر است، چون هزینه‌ی واقعی و
 * تکرارشونده را مسیرِ نوشتن اندازه می‌گیرد که عددش خام و واقعی است.
 */
export function noteCapture(c: DiagCost, sampleMs: number, every: number): DiagCost {
  const one = Math.min(Math.max(0, sampleMs), SAMPLE_CAP_MS);
  return { ...c, spentMs: c.spentMs + one * every };
}

/** آیا از این‌جا به بعد باید ساکت شد. */
export function tooExpensive(c: DiagCost): boolean {
  return c.slow >= MAX_SLOW_WRITES || c.spentMs > COST_BUDGET_MS;
}

/* ─────────────────────────── متنِ نهایی ─────────────────────────── */

export interface DiagMeta {
  session: string;
  appVersion: string | null;
  platform: string;
  /** ساعتِ دستگاه موقعِ ساختنِ متن. */
  at: string;
  /** چند سطر تا حالا ساخته شده — با سیصدتایی که مانده فرق دارد. */
  seen: number;
  /** اگر لاگ خودش را خاموش کرده، دلیلش. */
  stopped?: string;
}

/**
 * سرآیند + سطرهای این نشست + (اگر باشد) نشستِ قبلی.
 *
 * نشستِ قبلی برای همان حالتی است که این ابزار اصلاً برایش ساخته شده: اپ
 * بسته یا کرش شده، کاربر دوباره بازش کرده و رفته سراغِ «گزارشِ مشکل». اگر
 * فقط نشستِ جاری را می‌فرستادیم، گزارش دقیقاً از لحظه‌ای شروع می‌شد که
 * مشکل تمام شده بود.
 */
export const PREV_MARK = '── previous session ──';

/**
 * از محتوای فایلی که موقعِ بالا آمدن خوانده شده، فقط **خودِ نشستِ قبلی** را
 * نگه می‌دارد.
 *
 * ⚠ بدونِ این، لاگ خودش را تو در تو می‌کرد و دقیقاً برعکسِ چیزی که می‌خواهیم
 * از آب درمی‌آمد: فایلِ هر نشست خودش یک بخشِ «نشستِ قبلی» دارد، پس نشستِ
 * بعدی همان را هم به‌عنوانِ گذشته می‌خواند. و چون بریدن از **آخرِ** متن است
 * و آن بخش ته فایل می‌نشیند، بعد از چند بار باز و بسته کردنِ اپ، «نشستِ
 * قبلی» عملاً می‌شد نشستِ چند روز پیش — و سطرهای دیشب که دنبالشان بودی
 * افتاده بودند.
 */
export function lastSessionOnly(text: string | null): string {
  if (!text) return '';
  const at = text.indexOf(PREV_MARK);
  return (at < 0 ? text : text.slice(0, at)).trim();
}

export function diagText(
  meta: DiagMeta, lines: string[], previous = '', max = MAX_REPORT,
): string {
  const head = `${[
    `whosup diag · ${meta.session} · ${meta.at}`,
    `app ${meta.appVersion ?? '?'} · ${meta.platform} · ${meta.seen} rows`,
    ...(meta.stopped ? [`stopped: ${meta.stopped}`] : []),
  ].join('\n')}\n\n`;

  const prev = previous.trim()
    ? `\n\n${PREV_MARK}\n${tail(previous.trim(), MAX_PREVIOUS)}`
    : '';
  const room = Math.max(0, max - head.length - prev.length);
  return `${head}${tail(lines.join('\n'), room)}${prev}`;
}

/** آخرِ متن، نه اولش: تازه‌ترین سطرها همیشه از قدیمی‌ترین‌ها مهم‌ترند. */
function tail(text: string, max: number): string {
  if (text.length <= max) return text;
  if (max <= 2) return '';
  // دو کاراکترِ «…\n» هم توی سقف حساب می‌شود، وگرنه هر بار دو تا از سقف می‌زند
  // بیرون — که برای یک متنِ شصت‌کیلوبایتی بی‌اهمیت است و برای تستِ سقف نه.
  return `…\n${text.slice(text.length - (max - 2))}`;
}
